import { useColorMode, useColorModeValue, useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { darkenColor } from './utils';
import { useFilters, useShortcodeSettings } from './context';

import type { IToastOptions } from './types';

export const useCustomToast = () => {
	const toast = useToast();

	const showToast = useCallback(
		({ title, description, duration, isClosable, position, status = 'success' }: IToastOptions) => {
			toast({
				title: title,
				description: description,
				duration: duration ?? 9000,
				isClosable: isClosable ?? true,
				position: position ?? 'bottom-right',
				status,
			});
		},
		[toast]
	);

	return { showToast };
};

export const useIconColor = () => {
	return useColorModeValue('black', 'white');
};

export const useAutoContrastColor = () => {
	const ref = useRef(null);
	const [color, setColor] = useState('black');
	const { colorMode } = useColorMode();
	// Helper to find nearest non-transparent background
	const getBackgroundColor = (reactNode) => {
		let node = reactNode;
		while (node) {
			const bg = window.getComputedStyle(node).backgroundColor;
			if (bg && !bg.includes('rgba(0, 0, 0, 0)') && !bg.includes('transparent')) {
				return bg;
			}
			node = node.parentElement;
		}
		return 'rgb(255, 255, 255)'; // fallback: white
	};

	useEffect(() => {
		if (ref.current) {
			const bg = getBackgroundColor(ref.current);
			const rgb = bg.match(/\d+/g)?.map(Number);
			if (rgb && rgb.length >= 3) {
				const [r, g, b] = rgb;
				//Source: https://www.w3.org/TR/AERT/#color-contrast
				const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
				setColor(luminance > 0.6 ? 'black' : 'white');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [colorMode, ref?.current]);

	return { color, ref };
};

export const useDarkenInteractionStyles = (backgroundColor: string, hoverDarken: number, activeDarken: number) => {
	const hoverStyle = useMemo(() => {
		return {
			backgroundColor: darkenColor(backgroundColor, hoverDarken),
		};
	}, [backgroundColor, hoverDarken]);

	const activeStyle = useMemo(() => {
		return {
			backgroundColor: darkenColor(backgroundColor, activeDarken),
		};
	}, [backgroundColor, activeDarken]);

	return { hoverStyle, activeStyle };
};

export const useLocationFilterStates = () => {
	const { filters } = useFilters();
	const { settings: shortcodeSettings } = useShortcodeSettings();
	const { countries, states } = shortcodeSettings;

	return useMemo(() => {
		// If country/state is provided via shortcode/embedding script, treat it as "selected" for enabling dependent filters
		const hasCountrySelected = filters.selectedCountries.length > 0 || !!countries;
		const hasStateSelected = filters.selectedStates.length > 0 || !!states;

		const isStatesFilterDisabled = !hasCountrySelected;
		const isCitiesFilterDisabled = !hasStateSelected || !hasCountrySelected;

		return {
			hasCountrySelected,
			hasStateSelected,
			isStatesFilterDisabled,
			isCitiesFilterDisabled,
		};
	}, [filters.selectedCountries, filters.selectedStates, countries, states]);
};

export const useDebounce = <T extends unknown[]>(
	callback: ((...args: T) => void) | undefined,
	delay: number
): { debounced: (...args: T) => void; cancel: () => void } => {
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const callbackRef = useRef(callback);
	callbackRef.current = callback;

	const cancel = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	useEffect(() => {
		return cancel;
	}, [cancel]);

	const debounced = useCallback(
		(...args: T) => {
			if (!callbackRef.current) return;
			cancel();
			timerRef.current = setTimeout(() => {
				callbackRef.current?.(...args);
			}, delay);
		},
		[delay, cancel]
	);

	return { debounced, cancel };
};
